import { type FC, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import type { StoryFlowLockRecord } from "../../types/storyFlowLocks";
import type { TaskFlowEntry } from "../../utils/taskFlowGrouping";

import { useStoryFlowLocks } from "../../hooks/useStoryFlowLocks";
import {
  parseListParam,
  serializeListParam,
  updateSearchParams,
} from "../../utils/searchParams";
import { DataTable } from "../common/DataTable";
import { FlowDetailModal } from "./FlowDetailModal";
import { FlowGroupHeader } from "./FlowGroupHeader";
import { FlowTaskCard } from "./FlowTaskCard";
import { TaskRow } from "./TaskRow";

interface GroupedTasksViewProps {
  entries: TaskFlowEntry[];
  onRefresh?: () => void;
  onNavigateToIndexing?: (repoSlug: string) => void;
}

const FLOW_PARAM = "overview_flow";
const COLLAPSED_PARAM = "overview_collapsed";
const FLOW_NODE_PARAM = "overview_flow_node";
const QA_MANDATE_PARAM = "overview_qa_mandate";

function resolveExecutionRequestId(flowRunId: string): string | null {
  const trimmed = flowRunId.trim();
  if (!trimmed) {
    return null;
  }
  const separatorIndex = trimmed.indexOf(":");
  if (separatorIndex === -1) {
    return trimmed;
  }
  const prefix = trimmed.slice(0, separatorIndex);
  if (prefix !== "exec") {
    return null;
  }
  const rawId = trimmed.slice(separatorIndex + 1).trim();
  return rawId.length > 0 ? rawId : null;
}

export const GroupedTasksView: FC<GroupedTasksViewProps> = ({
  entries,
  onRefresh,
  onNavigateToIndexing,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedFlowId = searchParams.get(FLOW_PARAM);
  const selectedFlowNodeId = searchParams.get(FLOW_NODE_PARAM);
  const collapsedParam = searchParams.get(COLLAPSED_PARAM);
  const collapsedGroups = useMemo(
    () => new Set(parseListParam(collapsedParam)),
    [collapsedParam],
  );
  const { diagnostics, clearLock } = useStoryFlowLocks();

  const locksByTicket = useMemo(() => {
    const map = new Map<string, StoryFlowLockRecord>();
    if (diagnostics?.locks) {
      for (const lock of diagnostics.locks) {
        if (lock.storyIssueKey && lock.lockOwner) {
          map.set(lock.storyIssueKey, lock);
        }
      }
    }
    return map;
  }, [diagnostics]);

  const locksByExecutionFlowId = useMemo(() => {
    const map = new Map<string, StoryFlowLockRecord>();
    if (diagnostics?.locks) {
      for (const lock of diagnostics.locks) {
        if (
          lock.activeFlowType === "execution" &&
          lock.activeFlowId &&
          lock.lockOwner
        ) {
          map.set(lock.activeFlowId, lock);
        }
      }
    }
    return map;
  }, [diagnostics]);

  const toggleGroup = useCallback(
    (key: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const current = parseListParam(prev.get(COLLAPSED_PARAM));
          const collapsed = new Set(current);
          if (collapsed.has(key)) {
            collapsed.delete(key);
          } else {
            collapsed.add(key);
          }
          const serialized = serializeListParam(Array.from(collapsed));
          return updateSearchParams(next, { [COLLAPSED_PARAM]: serialized });
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleSelectFlow = useCallback(
    (flowRunId: string | null) => {
      setSearchParams(
        (prev) =>
          updateSearchParams(prev, {
            [FLOW_PARAM]: flowRunId,
            [FLOW_NODE_PARAM]: null,
            [QA_MANDATE_PARAM]: null,
          }),
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleSelectNode = useCallback(
    (nodeId: string | null) => {
      setSearchParams(
        (prev) => updateSearchParams(prev, { [FLOW_NODE_PARAM]: nodeId }),
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleClearLock = useCallback(
    (storyIssueKey: string) => {
      void clearLock(storyIssueKey);
    },
    [clearLock],
  );

  const resolveEntryLock = useCallback(
    (entry: { category: string; key: string; ticketId: string | null }) => {
      if (entry.category === "execution") {
        const executionRequestId = resolveExecutionRequestId(entry.key);
        if (executionRequestId) {
          const lock = locksByExecutionFlowId.get(executionRequestId);
          if (lock) {
            return lock;
          }
        }
      }
      return entry.ticketId
        ? (locksByTicket.get(entry.ticketId) ?? null)
        : null;
    },
    [locksByExecutionFlowId, locksByTicket],
  );

  const selectedFlow = useMemo(() => {
    if (!selectedFlowId) {
      return null;
    }
    const entry = entries.find(
      (candidate) =>
        candidate.kind === "group" && candidate.key === selectedFlowId,
    );
    if (entry?.kind !== "group") {
      return null;
    }
    const lock = resolveEntryLock(entry);
    return {
      category: entry.category,
      flowRunId: entry.key,
      lock,
      tasks: entry.tasks,
      ticketId: entry.ticketId,
    };
  }, [entries, resolveEntryLock, selectedFlowId]);

  // Hide Ticket/Node columns when no visible row has that field populated —
  // Dev and local environments often have these null across the board, leaving
  // Two permanently-empty columns.
  const { hasAnyTicket, hasAnyNode } = useMemo(() => {
    let ticket = false;
    let node = false;
    for (const entry of entries) {
      const tasks = entry.kind === "solo" ? [entry.task] : entry.tasks;
      for (const t of tasks) {
        if (t.linked_ticket_id) {
          ticket = true;
        }
        if (t.assigned_node) {
          node = true;
        }
        if (ticket && node) {
          break;
        }
      }
      if (ticket && node) {
        break;
      }
    }
    return { hasAnyNode: node, hasAnyTicket: ticket };
  }, [entries]);

  return (
    <>
      <DataTable maxHeight="600px">
        <DataTable.Header className="sticky top-0 z-10">
          <tr>
            <DataTable.HeaderCell>Task ID</DataTable.HeaderCell>
            <DataTable.HeaderCell>Status</DataTable.HeaderCell>
            <DataTable.HeaderCell className="hidden w-24 text-right md:table-cell">
              Cost
            </DataTable.HeaderCell>
            {hasAnyTicket && (
              <DataTable.HeaderCell className="hidden md:table-cell">
                Ticket
              </DataTable.HeaderCell>
            )}
            {hasAnyNode && (
              <DataTable.HeaderCell className="hidden md:table-cell">
                Node
              </DataTable.HeaderCell>
            )}
            <DataTable.HeaderCell className="hidden sm:table-cell">
              Updated
            </DataTable.HeaderCell>
            <DataTable.HeaderCell className="w-36 text-right">
              <span className="sr-only">Actions</span>
            </DataTable.HeaderCell>
          </tr>
        </DataTable.Header>

        {entries.map((entry) => {
          if (entry.kind === "solo") {
            return (
              <DataTable.Body key={entry.task.task_id}>
                <TaskRow
                  task={entry.task}
                  onRefresh={onRefresh}
                  hideTicketColumn={!hasAnyTicket}
                  hideNodeColumn={!hasAnyNode}
                />
              </DataTable.Body>
            );
          }

          const collapsed = collapsedGroups.has(entry.key);
          const lock = resolveEntryLock(entry);
          const visibleColumns =
            7 - (hasAnyTicket ? 0 : 1) - (hasAnyNode ? 0 : 1);

          return (
            <DataTable.RowGroup key={entry.key}>
              <FlowGroupHeader
                category={entry.category}
                ticketId={entry.ticketId}
                taskCount={entry.tasks.length}
                totalCostUsd={entry.totalCostUsd}
                statusCounts={entry.statusCounts}
                latestUpdatedAt={entry.latestUpdatedAt}
                collapsed={collapsed}
                onToggle={() => toggleGroup(entry.key)}
                lock={lock}
                onViewFlow={() => handleSelectFlow(entry.key)}
                repositorySlugs={entry.repositorySlugs}
                referenceRepositorySlugs={entry.referenceRepositorySlugs}
                onNavigateToIndexing={onNavigateToIndexing}
              />
              {!collapsed &&
                entry.tasks.map((task) => (
                  <FlowTaskCard
                    key={task.task_id}
                    task={task}
                    flowCategory={entry.category}
                    colSpan={visibleColumns}
                  />
                ))}
            </DataTable.RowGroup>
          );
        })}
      </DataTable>

      {selectedFlow && (
        <FlowDetailModal
          flowRunId={selectedFlow.flowRunId}
          category={selectedFlow.category}
          ticketId={selectedFlow.ticketId}
          tasks={selectedFlow.tasks}
          lock={selectedFlow.lock}
          onClearLock={handleClearLock}
          onClose={() => handleSelectFlow(null)}
          selectedNodeId={selectedFlowNodeId}
          onSelectNodeId={handleSelectNode}
        />
      )}
    </>
  );
};
